import { call } from '@rental/contracts/client'
import { format } from '@rental/i18n'
import { Button } from '@rental/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Car, Link2, Plus } from 'lucide-react'
import { type RefObject, useRef, useState } from 'react'
import { can } from '../access'
import { useAccess } from '../access-context'
import { api, load } from '../api'
import { OutcomeNotice } from '../components/OutcomeNotice'
import { PageHeader } from '../components/PageHeader'
import { Section } from '../components/Section'
import { StatusChip } from '../components/StatusChip'
import { Guard, LoadState } from '../components/states'
import { ProblemNotice } from '../components/ui'
import { useLocale } from '../i18n'
import { AddDialog, categoryName, EditDialog, LinkDialog } from '../models/dialogs'
import {
  arrange,
  type CategoryGroup,
  linkable,
  MODELS_QUERY_KEY,
  type ModelMap,
  modelLabel,
  type StaffModel,
  type StaffModels,
  writeBack,
} from '../models/models'
import { ModelPictureOf } from '../models/parts'
import { problemOf, type StaffProblem } from '../problems'

/**
 * Q-157 (the owner, 1 and 2 Nov): Categorie e modelli, for whoever holds
 * models.manage (the Owner and the Agents). Each category with the models
 * that stand for it on the website, the lead first; a model linked, moved,
 * made the lead, given a photo taken here, or added when the website lacks
 * it. Anyone else who types its address reads the refusal under its title.
 */
export function ModelsScreen() {
  const { messages: m } = useLocale()
  const { access } = useAccess()
  return (
    <Guard allowed={can(access, 'models.manage')} title={m.models.title}>
      <Models />
    </Guard>
  )
}

type Outcome = { key: number; text: string }

function Models() {
  const { messages: m } = useLocale()
  const words = m.models
  const queryClient = useQueryClient()
  const list = useQuery({
    queryKey: MODELS_QUERY_KEY,
    queryFn: () => load(call(api.GET('/v1/staff/models'))),
  })
  const [outcome, setOutcome] = useState<Outcome | null>(null)
  const [editing, setEditing] = useState<StaffModel | null>(null)
  const [linking, setLinking] = useState<CategoryGroup | null>(null)
  const [adding, setAdding] = useState(0)
  const editDialog = useRef<HTMLDialogElement>(null)
  const linkDialog = useRef<HTMLDialogElement>(null)
  const addDialog = useRef<HTMLDialogElement>(null)

  const said = (text: string) => {
    setOutcome((last) => ({ key: (last?.key ?? 0) + 1, text }))
    void queryClient.invalidateQueries({ queryKey: MODELS_QUERY_KEY })
  }

  return (
    <>
      <PageHeader
        title={words.title}
        intro={words.intro}
        actions={
          <Button variant="staff" disabled={!list.data} onClick={() => setAdding((n) => n + 1)}>
            <Plus aria-hidden="true" className="size-4" />
            {words.add}
          </Button>
        }
      />
      {outcome ? <OutcomeNotice key={outcome.key}>{outcome.text}</OutcomeNotice> : null}
      <LoadState data={list.data} problem={problemOf(list.error)}>
        {(data) => <Board data={data} onEdit={setEditing} onLink={setLinking} onSaid={said} />}
      </LoadState>
      {list.data ? (
        <Dialogs
          data={list.data}
          editing={editing}
          linking={linking}
          adding={adding}
          refs={{ editDialog, linkDialog, addDialog }}
          onSaid={said}
          onEditClosed={() => setEditing(null)}
          onLinkClosed={() => setLinking(null)}
        />
      ) : null}
    </>
  )
}

function Dialogs({
  data,
  editing,
  linking,
  adding,
  refs,
  onSaid,
  onEditClosed,
  onLinkClosed,
}: {
  data: StaffModels
  editing: StaffModel | null
  linking: CategoryGroup | null
  adding: number
  refs: {
    editDialog: RefObject<HTMLDialogElement | null>
    linkDialog: RefObject<HTMLDialogElement | null>
    addDialog: RefObject<HTMLDialogElement | null>
  }
  onSaid: (text: string) => void
  onEditClosed: () => void
  onLinkClosed: () => void
}) {
  const map = arrange(data)
  return (
    <>
      <EditDialog
        dialogRef={refs.editDialog}
        model={editing}
        map={map}
        onSaved={onSaid}
        onClose={onEditClosed}
      />
      <LinkDialog
        dialogRef={refs.linkDialog}
        group={linking}
        choices={linkable(data)}
        onLinked={onSaid}
        onClose={onLinkClosed}
      />
      <AddDialog dialogRef={refs.addDialog} opened={adding} map={map} onAdded={onSaid} />
    </>
  )
}

function Board({
  data,
  onEdit,
  onLink,
  onSaid,
}: {
  data: StaffModels
  onEdit: (model: StaffModel) => void
  onLink: (group: CategoryGroup) => void
  onSaid: (text: string) => void
}) {
  const { locale, messages: m } = useLocale()
  const words = m.models
  const map: ModelMap = arrange(data)
  return (
    <div className="flex flex-col gap-6">
      {map.groups.map((group) => {
        const name = categoryName(group.category, locale)
        return (
          <Section
            key={group.category.id}
            title={name}
            status={
              group.category.active ? null : (
                <StatusChip tone="neutral" compact>
                  {words.inactiveCategory}
                </StatusChip>
              )
            }
            actions={
              <Button variant="outline" onClick={() => onLink(group)}>
                <Link2 aria-hidden="true" className="size-4" />
                {words.link}
              </Button>
            }
          >
            {group.models.length === 0 ? (
              <div className="flex items-start gap-3 text-text-muted">
                <Car aria-hidden="true" className="mt-1 size-4 shrink-0" />
                <div>
                  <p className="font-medium text-text">{words.noModels}</p>
                  <p className="text-sm">{words.noModelsHint}</p>
                </div>
              </div>
            ) : (
              <>
                {group.lead ? null : <p className="text-sm text-text-muted">{words.noLead}</p>}
                <ModelList models={group.models} category={name} onEdit={onEdit} onSaid={onSaid} />
              </>
            )}
          </Section>
        )
      })}
      {map.unlinked.length > 0 ? (
        <Section title={words.unlinked}>
          <p className="text-sm text-text-muted">{words.unlinkedHint}</p>
          <ModelList models={map.unlinked} category={null} onEdit={onEdit} onSaid={onSaid} />
        </Section>
      ) : null}
      {map.retired.length > 0 ? (
        <Section title={words.retired}>
          <ModelList models={map.retired} category={null} onEdit={onEdit} onSaid={onSaid} />
        </Section>
      ) : null}
    </div>
  )
}

/** A category's models as cards: the picture, the name, what the model is, its actions. */
function ModelList({
  models,
  category,
  onEdit,
  onSaid,
}: {
  models: readonly StaffModel[]
  /** The category's name, for a lead taken here; null outside a category. */
  category: string | null
  onEdit: (model: StaffModel) => void
  onSaid: (text: string) => void
}) {
  const { messages: m } = useLocale()
  const words = m.models
  const { takeOver } = useAccess()
  const [busy, setBusy] = useState<string | null>(null)
  const [problem, setProblem] = useState<StaffProblem | null>(null)

  async function makeLead(model: StaffModel) {
    if (busy) return
    setProblem(null)
    setBusy(model.id)
    const result = await call(
      api.PUT('/v1/staff/models/{id}', {
        params: { path: { id: model.id } },
        body: writeBack(model, { isLead: true }),
      }),
    )
    setBusy(null)
    if (!result.ok) {
      if (!takeOver(result.problem)) setProblem(result.problem)
      return
    }
    onSaid(
      format(words.madeLead, {
        model: modelLabel(model, m.transmissions),
        category: category ?? '',
      }),
    )
  }

  return (
    <div className="flex flex-col gap-3">
      <ProblemNotice problem={problem} />
      <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {models.map((model) => {
          const name = modelLabel(model, m.transmissions)
          return (
            <li
              key={model.id}
              className="flex flex-col gap-2"
              data-model={model.siteRef ?? model.id}
            >
              <ModelPictureOf model={model} />
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{name}</span>
                {model.isLead ? (
                  <StatusChip tone="success" compact>
                    {words.lead}
                  </StatusChip>
                ) : null}
                {model.siteRef === null ? (
                  <StatusChip tone="info" compact>
                    {words.appModel}
                  </StatusChip>
                ) : model.photoMediaId ? (
                  <StatusChip tone="info" compact>
                    {words.ownPhoto}
                  </StatusChip>
                ) : null}
              </div>
              <div className="flex flex-wrap gap-2">
                {category !== null && !model.isLead ? (
                  <Button
                    variant="outline"
                    busy={busy === model.id}
                    disabled={busy !== null}
                    aria-label={`${words.makeLead}: ${name}`}
                    onClick={() => void makeLead(model)}
                  >
                    {words.makeLead}
                  </Button>
                ) : null}
                <Button
                  variant="outline"
                  disabled={busy !== null}
                  aria-label={`${words.edit}: ${name}`}
                  onClick={() => onEdit(model)}
                >
                  {words.edit}
                </Button>
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
